import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more.js';
import NoDataToDisplayModule from 'highcharts/modules/no-data-to-display.js';
import SolidGaugeModule from 'highcharts/modules/solid-gauge.js';
import StockModule from 'highcharts/modules/stock.js';
import XRangeModule from 'highcharts/modules/xrange.js';

HighchartsMore(Highcharts);
SolidGaugeModule(Highcharts);
NoDataToDisplayModule(Highcharts);
StockModule(Highcharts);
XRangeModule(Highcharts);

import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { isEqual } from 'lodash-es';

import { Tab } from '@vi-iot/ui';
import { TimeRangeTabNameEnum } from '@vi-iot/ui/components/tabs/tabs.component';

import { EnergyHistoricalDataStore } from '@store';
import { GTMService } from '../../../../shared/services';
import { AlignType } from '../../models/align-type.model';
import { Chart, ChartBarModel, ChartBarPartModel, ChartPeriod } from '../../models/chart.model';

export interface BarWidthInterface {
  width: string;
  minWidth?: string;
}
@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements OnChanges {
  @Input() alignNumbers?: AlignType;
  @Input() chartData: Chart;

  timeRangeTabNameEnum = TimeRangeTabNameEnum;
  tabs: Tab[] = [];
  activeTab: string;
  barWidth: number;
  activeBar: ChartBarModel;
  activeBarIndex: number;

  constructor(private gtmService: GTMService, public energyStore: EnergyHistoricalDataStore) {}

  ngOnChanges(changes: SimpleChanges): void {
    const chartDataPresent = changes.chartData && changes.chartData.currentValue;
    if (chartDataPresent) {
      const chartValuesChanged = !isEqual(
        changes.chartData.currentValue,
        changes.chartData.previousValue
      );
      if (chartValuesChanged) {
        this.setTabs(changes.chartData.currentValue.periods);
        this.setActiveBarDefault();
      }
    }
  }

  onTimeRangeChange(activeTabValue: string): void {
    this.activeTab = activeTabValue;
    this.setActiveBarDefault();

    this.gtmService.track('onSelect', 'Time range tab', 'consumptionChart');
  }

  getBarWidth(amountOfBars: number): BarWidthInterface {
    if (amountOfBars < 13) {
      return { width: 100 / amountOfBars + '%' };
    }

    return { width: '100%', minWidth: '45px' };
  }

  filterBarParts(barParts: ChartBarPartModel[]): ChartBarPartModel[] {
    return barParts.filter((bar) => bar.color);
  }

  hideBarLabel(index: number, barAmount: number): boolean {
    const numberOfBarsWhichFitsTheXsScreen = 5;
    if (barAmount > numberOfBarsWhichFitsTheXsScreen) {
      if (barAmount % 2) {
        return 0 !== index % 2;
      } else {
        return 0 === index % 2;
      }
    }

    return false;
  }

  setActiveBar(bar: ChartBarModel, index: number): void {
    this.activeBar = bar;
    this.activeBarIndex = index;
  }

  setActiveBarDefault(): void {
    if (this.chartData) {
      const chartBars = this.chartData.periods.find(
        (p) => p.periodDesignationKey === this.activeTab
      )?.chartBars;
      const chartBarsLength = chartBars?.length ?? 0;
      const activeBar = chartBars?.find((e) => !!e);
      this.setActiveBar(activeBar, chartBarsLength > 0 ? chartBarsLength - 1 : 0);
    }
  }

  private setTabs(periods: ChartPeriod[]): void {
    this.tabs = [];
    periods.forEach((period, index) => {
      this.tabs.push({
        name: period.periodDesignationKey,
        active: index === 0,
        value: period.periodDesignationKey,
      });
    });
    this.setActiveTab();
  }

  private setActiveTab(): void {
    if (this.tabs.length > 0) {
      this.activeTab = this.tabs.filter((t) => t.active).pop().value;
    }
  }
}
